import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    Typography, Box, withStyles,
    ExpansionPanel, ExpansionPanelSummary, ExpansionPanelDetails
} from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import { getAiGradingReport } from "./aiGradingActions";

const severityColor = (severity) => {
    switch (severity) {
        case 'critical': return '#CC0000';
        case 'major': return '#E55934';
        case 'moderate': return '#ffb300';
        case 'minor': return '#7cb342';
        default: return '#B3B3B3';
    }
};

const styles = theme => ({
    root: { marginTop: theme.spacing(0.5), marginBottom: theme.spacing(1) },
    aiTag: {
        display: 'inline-flex', alignItems: 'center',
        color: theme.palette.primary.main, fontWeight: 500,
    },
    heading: { fontSize: theme.typography.pxToRem(14), fontWeight: 500 },
    expand: {
        marginTop: theme.spacing(0.5), boxShadow: 'none',
        border: '1px solid rgba(0, 0, 0, 0.12)', '&:before': { display: 'none' },
    },
    findingRow: { display: 'flex', alignItems: 'flex-start', marginTop: theme.spacing(0.5) },
    severityDot: { width: 10, height: 10, borderRadius: 5, marginTop: 5, marginRight: 8, flexShrink: 0 },
    mismatch: { color: '#E55934', marginTop: theme.spacing(0.5) },
    retake: { color: '#b26a00', marginTop: theme.spacing(0.5) },
});

const AiGradingPanel = ({ classes, currentGradeId, grades }) => {
    const dispatch = useDispatch();
    const activeCustomer = useSelector(state => state?.core?.login?.activeCustomer?.CustomerID ?? null);
    const translations = useSelector(state => state?.core?.translation?.translations ?? {});
    const [report, setReport] = useState(null);

    const productID = new URLSearchParams(window.location.search).get('productID');

    useEffect(() => {
        let mounted = true;
        const load = async () => {
            if (!productID || !activeCustomer) return;
            const result = await dispatch(getAiGradingReport({ ProductID: productID, ActiveCustomerID: activeCustomer }));
            if (mounted) setReport(result);
        };
        load();
        return () => { mounted = false; };
    }, [productID, activeCustomer, dispatch]);

    // Only annotate the grade when the device actually has an AI report. Manually
    // graded devices show nothing here — the grade dropdown stands on its own.
    if (!report) return null;

    const insufficient = report.RetakeState === 'INSUFFICIENT_DATA';
    const confidence = typeof report.OverallConfidence === 'number' ? `${Math.round(report.OverallConfidence * 100)}%` : null;
    const findings = report.Findings || [];

    // The grade dropdown is the single source of truth. "Grade set by AI" holds only
    // while the AI applied the grade AND the live grade still equals the AI's grade.
    // An untested ("U") or operator-changed grade won't match the AI's grade, so the
    // annotation disappears — on fresh load and on edit alike.
    const selected = (grades || []).find((g) => String(g.value) === String(currentGradeId));
    const currentLetter = selected ? String(selected.displayValue).split(' (')[0].trim().toUpperCase() : '';
    const aiLetter = String(report.OverallGrade || '').trim().toUpperCase();
    const matchesApplied = !!report.AppliedToProduct && !!currentLetter && currentLetter === aiLetter;

    if (!matchesApplied && !insufficient) return null;

    const tagText = insufficient
        ? (translations['ai_insufficient'] || 'AI could not grade — more photos needed')
        : (translations['graded_by_ai'] || 'Grade set by AI') + (confidence ? ` · ${confidence}` : '');

    return (
        <div className={classes.root}>
            <Typography variant="caption" className={classes.aiTag}>
                <span role="img" aria-label="ai" style={{ marginRight: 6 }}>✨</span>{tagText}
            </Typography>

            {report.ModelMismatch ? (
                <Typography variant="body2" className={classes.mismatch}>
                    ⚠ {translations['model_mismatch'] || 'Model mismatch'}: scanner "{report.SuspectedModel}" vs AI "{report.ModelIdentified}"
                </Typography>
            ) : null}

            {report.RetakeInstructions && report.RetakeInstructions.length ? (
                <Box mt={1}>
                    <Typography variant="body2" className={classes.retake}>
                        {translations['retake_suggested'] || 'Retake suggested'}:
                    </Typography>
                    {report.RetakeInstructions.map((line, i) => (
                        <Typography key={i} variant="caption" display="block" className={classes.retake}>• {line}</Typography>
                    ))}
                </Box>
            ) : null}

            {findings.length ? (
                <ExpansionPanel className={classes.expand}>
                    <ExpansionPanelSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography className={classes.heading}>
                            {translations['ai_grading_details'] || 'AI grading details'} ({findings.length})
                        </Typography>
                    </ExpansionPanelSummary>
                    <ExpansionPanelDetails style={{ display: 'block' }}>
                        {findings.map((f, i) => (
                            <div key={i} className={classes.findingRow}>
                                <span className={classes.severityDot} style={{ backgroundColor: severityColor(f.Severity) }} />
                                <div>
                                    <Typography variant="body2" style={{ fontWeight: 600 }}>
                                        {f.Title} <span style={{ color: '#888', fontWeight: 'normal' }}>({f.Area} · {f.Severity})</span>
                                    </Typography>
                                    <Typography variant="caption" color="textSecondary">{f.Description}</Typography>
                                </div>
                            </div>
                        ))}
                    </ExpansionPanelDetails>
                </ExpansionPanel>
            ) : null}
        </div>
    );
};

export default withStyles(styles)(AiGradingPanel);
